import { lazy } from "react";
import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { createBrowserRouter, Navigate } from "react-router-dom";
//updated on 29-05
import { listBudgetAuthorityConfig } from "../api/budgets";
import { ProtectedRoute } from "../components/auth/ProtectedRoute";
import { RequireContext } from "../components/auth/RequireContext";
import { RequirePermission } from "../components/auth/RequirePermission";
import { AppShell } from "../components/layout/AppShell";
import { RequireReportAccess } from "../components/reports/RequireReportAccess";
import type { ReportId } from "../components/reports/reportAccess";
import { useAuth } from "../hooks/useAuth";
import { FORM_IDS, PROCESS_IDS } from "../lib/permissionCatalog";
import {
  canAccessBudgetAllocation,
  canViewBudgets,
  canViewBudgetWorksheets,
} from "../lib/purchaseAccess";

const LoginPage = lazy(async () => {
  const module = await import("../pages/auth/LoginPage");
  return { default: module.LoginPage };
});

const ForgotPasswordPage = lazy(async () => {
  const module = await import("../pages/auth/ForgotPasswordPage");
  return { default: module.ForgotPasswordPage };
});

const PrivacyPolicyPage = lazy(async () => {
  const module = await import("../pages/auth/PrivacyPolicyPage");
  return { default: module.PrivacyPolicyPage };
});

const DeleteAccountPage = lazy(async () => {
  const module = await import("../pages/auth/DeleteAccountPage");
  return { default: module.DeleteAccountPage };
});

const DashboardPage = lazy(async () => {
  const module = await import("../pages/dashboards/DashboardPage");
  return { default: module.DashboardPage };
});

const RequisitionListPage = lazy(async () => {
  const module = await import("../pages/requisitions/RequisitionListPage");
  return { default: module.RequisitionListPage };
});

const RequisitionCreatePage = lazy(async () => {
  const module = await import("../pages/requisitions/RequisitionCreatePage");
  return { default: module.RequisitionCreatePage };
});

const RequisitionDetailPage = lazy(async () => {
  const module = await import("../pages/requisitions/RequisitionDetailPage");
  return { default: module.RequisitionDetailPage };
});

const RequisitionEditPage = lazy(async () => {
  const module = await import("../pages/requisitions/RequisitionEditPage");
  return { default: module.RequisitionEditPage };
});

const HODReviewPage = lazy(async () => {
  const module = await import("../pages/requisitions/HODReviewPage");
  return { default: module.HODReviewPage };
});

const RequisitionReviewPage = lazy(async () => {
  const module = await import("../pages/requisitions/RequisitionReviewPage");
  return { default: module.RequisitionReviewPage };
});

const RequisitionTimelinePage = lazy(async () => {
  const module = await import("../pages/requisitions/RequisitionTimelinePage");
  return { default: module.RequisitionTimelinePage };
});

const RFQListPage = lazy(async () => {
  const module = await import("../pages/rfqs/RFQListPage");
  return { default: module.RFQListPage };
});

const RFQCreatePage = lazy(async () => {
  const module = await import("../pages/rfqs/RFQCreatePage");
  return { default: module.RFQCreatePage };
});

const RFQDetailPage = lazy(async () => {
  const module = await import("../pages/rfqs/RFQDetailPage");
  return { default: module.RFQDetailPage };
});

const RFQResponseEntryPage = lazy(async () => {
  const module = await import("../pages/rfqs/RFQResponseEntryPage");
  return { default: module.RFQResponseEntryPage };
});

const QuotationComparisonPage = lazy(async () => {
  const module = await import("../pages/rfqs/QuotationComparisonPage");
  return { default: module.QuotationComparisonPage };
});

const VendorListPage = lazy(async () => {
  const module = await import("../pages/vendors/VendorListPage");
  return { default: module.VendorListPage };
});

const VendorCreatePage = lazy(async () => {
  const module = await import("../pages/vendors/VendorCreatePage");
  return { default: module.VendorCreatePage };
});

const VendorDetailPage = lazy(async () => {
  const module = await import("../pages/vendors/VendorDetailPage");
  return { default: module.VendorDetailPage };
});

const VendorEditPage = lazy(async () => {
  const module = await import("../pages/vendors/VendorEditPage");
  return { default: module.VendorEditPage };
});

const VendorApprovePage = lazy(async () => {
  const module = await import("../pages/vendors/VendorApprovePage");
  return { default: module.VendorApprovePage };
});

const ItemsListPage = lazy(async () => {
  const module = await import("../pages/items/ItemsListPage");
  return { default: module.ItemsListPage };
});

const ItemCreatePage = lazy(async () => {
  const module = await import("../pages/items/ItemCreatePage");
  return { default: module.ItemCreatePage };
});

const ItemEditPage = lazy(async () => {
  const module = await import("../pages/items/ItemEditPage");
  return { default: module.ItemEditPage };
});

const InventoryAdminPage = lazy(async () => {
  const module = await import("../pages/inventory/InventoryAdminPage");
  return { default: module.InventoryAdminPage };
});

const ServiceListPage = lazy(async () => {
  const module = await import("../pages/services/ServiceListPage");
  return { default: module.ServiceListPage };
});

const ServiceCreatePage = lazy(async () => {
  const module = await import("../pages/services/ServiceCreatePage");
  return { default: module.ServiceCreatePage };
});

const ServiceEditPage = lazy(async () => {
  const module = await import("../pages/services/ServiceEditPage");
  return { default: module.ServiceEditPage };
});

const ServiceRequestReviewPage = lazy(async () => {
  const module = await import("../pages/services/ServiceRequestReviewPage");
  return { default: module.ServiceRequestReviewPage };
});

const VendorContactsPage = lazy(async () => {
  const module = await import("../pages/vendors/VendorContactsPage");
  return { default: module.VendorContactsPage };
});

const VendorPerformancePage = lazy(async () => {
  const module = await import("../pages/vendors/VendorPerformancePage");
  return { default: module.VendorPerformancePage };
});

const VendorStatementOfAccountPage = lazy(async () => {
  const module = await import("../pages/vendors/VendorStatementOfAccountPage");
  return { default: module.VendorStatementOfAccountPage };
});

const PurchaseOrderListPage = lazy(async () => {
  const module = await import("../pages/purchase-orders/PurchaseOrderListPage");
  return { default: module.PurchaseOrderListPage };
});

const PurchaseOrderCreatePage = lazy(async () => {
  const module = await import("../pages/purchase-orders/PurchaseOrderCreatePage");
  return { default: module.PurchaseOrderCreatePage };
});

const PurchaseOrderCreateDirectPage = lazy(async () => {
  const module = await import("../pages/purchase-orders/PurchaseOrderCreateDirectPage");
  return { default: module.PurchaseOrderCreateDirectPage };
});

const PurchaseOrderDetailPage = lazy(async () => {
  const module = await import("../pages/purchase-orders/PurchaseOrderDetailPage");
  return { default: module.PurchaseOrderDetailPage };
});

const PurchaseOrderEditPage = lazy(async () => {
  const module = await import("../pages/purchase-orders/PurchaseOrderEditPage");
  return { default: module.PurchaseOrderEditPage };
});

const PurchaseOrderApprovalPage = lazy(async () => {
  const module = await import("../pages/purchase-orders/PurchaseOrderApprovalPage");
  return { default: module.PurchaseOrderApprovalPage };
});

const PurchaseOrderRevisionsPage = lazy(async () => {
  const module = await import("../pages/purchase-orders/PurchaseOrderRevisionsPage");
  return { default: module.PurchaseOrderRevisionsPage };
});

const ContractsListPage = lazy(async () => {
  const module = await import("../pages/contracts/ContractsListPage");
  return { default: module.ContractsListPage };
});

const ContractCreatePage = lazy(async () => {
  const module = await import("../pages/contracts/ContractCreatePage");
  return { default: module.ContractCreatePage };
});

const ContractEditPage = lazy(async () => {
  const module = await import("../pages/contracts/ContractEditPage");
  return { default: module.ContractEditPage };
});

const ContractDetailPage = lazy(async () => {
  const module = await import("../pages/contracts/ContractDetailPage");
  return { default: module.ContractDetailPage };
});

const InvoiceRegisterPage = lazy(async () => {
  const module = await import("../pages/invoices/InvoiceRegisterPage");
  return { default: module.InvoiceRegisterPage };
});

const InvoiceCreatePage = lazy(async () => {
  const module = await import("../pages/invoices/InvoiceCreatePage");
  return { default: module.InvoiceCreatePage };
});

const InvoiceDetailPage = lazy(async () => {
  const module = await import("../pages/invoices/InvoiceDetailPage");
  return { default: module.InvoiceDetailPage };
});

const InvoiceEditPage = lazy(async () => {
  const module = await import("../pages/invoices/InvoiceEditPage");
  return { default: module.InvoiceEditPage };
});

const InvoiceVerifyPage = lazy(async () => {
  const module = await import("../pages/invoices/InvoiceVerifyPage");
  return { default: module.InvoiceVerifyPage };
});

const InvoiceApprovePage = lazy(async () => {
  const module = await import("../pages/invoices/InvoiceApprovePage");
  return { default: module.InvoiceApprovePage };
});

const InvoicePaymentPage = lazy(async () => {
  const module = await import("../pages/invoices/InvoicePaymentPage");
  return { default: module.InvoicePaymentPage };
});

const InvoicePaymentBatchReviewPage = lazy(async () => {
  const module = await import("../pages/invoices/InvoicePaymentBatchReviewPage");
  return { default: module.InvoicePaymentBatchReviewPage };
});

const InvoiceConfigurationPage = lazy(async () => {
  const module = await import("../pages/invoices/InvoiceConfigurationPage");
  return { default: module.InvoiceConfigurationPage };
});

const CreditNoteListPage = lazy(async () => {
  const module = await import("../pages/credit-notes/CreditNoteListPage");
  return { default: module.CreditNoteListPage };
});

const CreditNoteDetailPage = lazy(async () => {
  const module = await import("../pages/credit-notes/CreditNoteDetailPage");
  return { default: module.CreditNoteDetailPage };
});

const BudgetOverviewPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetOverviewPage");
  return { default: module.BudgetOverviewPage };
});

const BudgetPlanListPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetPlanListPage");
  return { default: module.BudgetPlanListPage };
});

const BudgetPlanCreatePage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetPlanCreatePage");
  return { default: module.BudgetPlanCreatePage };
});

const BudgetPlanDetailPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetPlanDetailPage");
  return { default: module.BudgetPlanDetailPage };
});

const DrydockBudgetListPage = lazy(async () => {
  const module = await import("../pages/budgets/DrydockBudgetListPage");
  return { default: module.DrydockBudgetListPage };
});

const ProjectsPage = lazy(async () => {
  const module = await import("../pages/projects/ProjectsPage");
  return { default: module.ProjectsPage };
});

const ProjectDetailPage = lazy(async () => {
  const module = await import("../pages/projects/ProjectDetailPage");
  return { default: module.ProjectDetailPage };
});

const DrydockBudgetCreatePage = lazy(async () => {
  const module = await import("../pages/budgets/DrydockBudgetCreatePage");
  return { default: module.DrydockBudgetCreatePage };
});

const DrydockWorksheetPage = lazy(async () => {
  const module = await import("../pages/budgets/DrydockWorksheetPage");
  return { default: module.DrydockWorksheetPage };
});

const DrydockTrackingPage = lazy(async () => {
  const module = await import("../pages/budgets/DrydockTrackingPage");
  return { default: module.DrydockTrackingPage };
});

const PredeliveryBudgetListPage = lazy(async () => {
  const module = await import("../pages/budgets/PredeliveryBudgetListPage");
  return { default: module.PredeliveryBudgetListPage };
});

const PredeliveryBudgetCreatePage = lazy(async () => {
  const module = await import("../pages/budgets/PredeliveryBudgetCreatePage");
  return { default: module.PredeliveryBudgetCreatePage };
});

const RecoveryEntryListPage = lazy(async () => {
  const module = await import("../pages/budgets/RecoveryEntryListPage");
  return { default: module.RecoveryEntryListPage };
});

const RecoveryEntryCreatePage = lazy(async () => {
  const module = await import("../pages/budgets/RecoveryEntryCreatePage");
  return { default: module.RecoveryEntryCreatePage };
});

const PredeliveryWorksheetPage = lazy(async () => {
  const module = await import("../pages/budgets/PredeliveryWorksheetPage");
  return { default: module.PredeliveryWorksheetPage };
});

const PredeliveryTrackingPage = lazy(async () => {
  const module = await import("../pages/budgets/PredeliveryTrackingPage");
  return { default: module.PredeliveryTrackingPage };
});

const BudgetWorksheetPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetWorksheetPage");
  return { default: module.BudgetWorksheetPage };
});

const BudgetSubmitPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetSubmitPage");
  return { default: module.BudgetSubmitPage };
});

const BudgetPlanRevisePage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetPlanRevisePage");
  return { default: module.BudgetPlanRevisePage };
});

const BudgetActivatePage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetActivatePage");
  return { default: module.BudgetActivatePage };
});

const BudgetApprovePage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetApprovePage");
  return { default: module.BudgetApprovePage };
});

const BudgetApprovalsPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetApprovalsPage");
  return { default: module.BudgetApprovalsPage };
});

const BudgetExclusionsPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetExclusionsPage");
  return { default: module.BudgetExclusionsPage };
});

const BudgetAllocatePage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetAllocatePage");
  return { default: module.BudgetAllocatePage };
});

const BudgetRevisePage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetRevisePage");
  return { default: module.BudgetRevisePage };
});

const BudgetCloseYearPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetCloseYearPage");
  return { default: module.BudgetCloseYearPage };
});

const OwnerApprovalsPage = lazy(async () => {
  const module = await import("../pages/owner/OwnerApprovalsPage");
  return { default: module.OwnerApprovalsPage };
});

const OwnerVesselDetailPage = lazy(async () => {
  const module = await import("../pages/owner/OwnerVesselDetailPage");
  return { default: module.OwnerVesselDetailPage };
});

const BudgetDetailPage = lazy(async () => {
  const module = await import("../pages/budgets/BudgetDetailPage");
  return { default: module.BudgetDetailPage };
});

const ExchangeRateListPage = lazy(async () => {
  const module = await import("../pages/exchange-rates/ExchangeRateListPage");
  return { default: module.ExchangeRateListPage };
});

const ExchangeRateEntryPage = lazy(async () => {
  const module = await import("../pages/exchange-rates/ExchangeRateEntryPage");
  return { default: module.ExchangeRateEntryPage };
});

const ExchangeRateHistoryPage = lazy(async () => {
  const module = await import("../pages/exchange-rates/ExchangeRateHistoryPage");
  return { default: module.ExchangeRateHistoryPage };
});

const DelegationListPage = lazy(async () => {
  const module = await import("../pages/delegations/DelegationListPage");
  return { default: module.DelegationListPage };
});

const DelegationCreatePage = lazy(async () => {
  const module = await import("../pages/delegations/DelegationCreatePage");
  return { default: module.DelegationCreatePage };
});

const UserManagementPage = lazy(async () => {
  const module = await import("../pages/admin/UserManagementPage");
  return { default: module.UserManagementPage };
});

const UserCreatePage = lazy(async () => {
  const module = await import("../pages/admin/UserManagementPage");
  return { default: module.UserCreatePage };
});

const UserEditPage = lazy(async () => {
  const module = await import("../pages/admin/UserManagementPage");
  return { default: module.UserEditPage };
});

const RoleAssignmentPage = lazy(async () => {
  const module = await import("../pages/admin/RoleAssignmentPage");
  return { default: module.RoleAssignmentPage };
});

const SystemConfigPage = lazy(async () => {
  const module = await import("../pages/admin/SystemConfigPage");
  return { default: module.SystemConfigPage };
});

const ApprovalChainConfigPage = lazy(async () => {
  const module = await import("../pages/admin/ApprovalChainConfigPage");
  return { default: module.ApprovalChainConfigPage };
});

const AccountCodeManagementPage = lazy(async () => {
  const module = await import("../pages/admin/AccountCodeManagementPage");
  return { default: module.AccountCodeManagementPage };
});

const AccountCodeCreatePage = lazy(async () => {
  const module = await import("../pages/admin/AccountCodeManagementPage");
  return { default: module.AccountCodeCreatePage };
});

const AccountCodeEditPage = lazy(async () => {
  const module = await import("../pages/admin/AccountCodeManagementPage");
  return { default: module.AccountCodeEditPage };
});

const ExpenditureTypeCreatePage = lazy(async () => {
  const module = await import("../pages/admin/AccountCodeManagementPage");
  return { default: module.ExpenditureTypeCreatePage };
});

const ExpenditureTypeEditPage = lazy(async () => {
  const module = await import("../pages/admin/AccountCodeManagementPage");
  return { default: module.ExpenditureTypeEditPage };
});

const ContractApprovalChainConfigPage = lazy(async () => {
  const module = await import("../pages/admin/ContractApprovalChainConfigPage");
  return { default: module.ContractApprovalChainConfigPage };
});

const TrackerDetailPage = lazy(async () => {
  const module = await import("../pages/tracker/TrackerDetailPage");
  return { default: module.TrackerDetailPage };
});

const GRNListPage = lazy(async () => {
  const module = await import("../pages/grns/GRNListPage");
  return { default: module.GRNListPage };
});

const GRNCreatePage = lazy(async () => {
  const module = await import("../pages/grns/GRNCreatePage");
  return { default: module.GRNCreatePage };
});

const ServiceCompletionCreatePage = lazy(async () => {
  const module = await import("../pages/service-completions/ServiceCompletionCreatePage");
  return { default: module.ServiceCompletionCreatePage };
});

const ServiceCompletionDetailPage = lazy(async () => {
  const module = await import("../pages/service-completions/ServiceCompletionDetailPage");
  return { default: module.ServiceCompletionDetailPage };
});

const GRNDetailPage = lazy(async () => {
  const module = await import("../pages/grns/GRNDetailPage");
  return { default: module.GRNDetailPage };
});

const NotificationCenterPage = lazy(async () => {
  const module = await import("../pages/notifications/NotificationCenterPage");
  return { default: module.NotificationCenterPage };
});

const MyCommentsPage = lazy(async () => {
  const module = await import("../pages/notifications/MyCommentsPage");
  return { default: module.MyCommentsPage };
});

const RequisitionRegisterReportPage = lazy(async () => {
  const module = await import("../pages/reports/RequisitionRegisterReportPage");
  return { default: module.RequisitionRegisterReportPage };
});

const PORegisterReportPage = lazy(async () => {
  const module = await import("../pages/reports/PORegisterReportPage");
  return { default: module.PORegisterReportPage };
});

const VendorPerformanceReportPage = lazy(async () => {
  const module = await import("../pages/reports/VendorPerformanceReportPage");
  return { default: module.VendorPerformanceReportPage };
});

const VendorStatementOfAccountReportPage = lazy(async () => {
  const module = await import("../pages/reports/VendorStatementOfAccountReportPage");
  return { default: module.VendorStatementOfAccountReportPage };
});

const BudgetUtilizationReportPage = lazy(async () => {
  const module = await import("../pages/reports/BudgetUtilizationReportPage");
  return { default: module.BudgetUtilizationReportPage };
});

const InvoiceAgingReportPage = lazy(async () => {
  const module = await import("../pages/reports/InvoiceAgingReportPage");
  return { default: module.InvoiceAgingReportPage };
});

const PaidAwaitingDeliveryReportPage = lazy(async () => {
  const module = await import("../pages/reports/PaidAwaitingDeliveryReportPage");
  return { default: module.PaidAwaitingDeliveryReportPage };
});

const SupplyByPortReportPage = lazy(async () => {
  const module = await import("../pages/reports/SupplyByPortReportPage");
  return { default: module.SupplyByPortReportPage };
});

const AuditLogPage = lazy(async () => {
  const module = await import("../pages/audit/AuditLogPage");
  return { default: module.AuditLogPage };
});

const AuditExportPage = lazy(async () => {
  const module = await import("../pages/audit/AuditExportPage");
  return { default: module.AuditExportPage };
});

function withProtectedShell(element: ReactElement) {
  return (
    <ProtectedRoute>
      <AppShell>{element}</AppShell>
    </ProtectedRoute>
  );
}

function withProtectedShellAndPermissions(
  element: ReactElement,
  type: "office" | "crew",
  options: {
    roles?: string[];
    forms?: string[];
    processes?: string[];
    anyOf?: Array<{
      roles?: string[];
      forms?: string[];
      processes?: string[];
    }>;
  },
) {
  return (
    <ProtectedRoute>
      <RequireContext type={type}>
        <RequirePermission {...options}>
          <AppShell>{element}</AppShell>
        </RequirePermission>
      </RequireContext>
    </ProtectedRoute>
  );
}

function withProtectedShellAndPermissionsAny(
  element: ReactElement,
  options: {
    roles?: string[];
    forms?: string[];
    processes?: string[];
    anyOf?: Array<{
      roles?: string[];
      forms?: string[];
      processes?: string[];
    }>;
  },
) {
  return (
    <ProtectedRoute>
      <RequirePermission {...options}>
        <AppShell>{element}</AppShell>
      </RequirePermission>
    </ProtectedRoute>
  );
}

function withProtectedReport(element: ReactElement, reportId: ReportId) {
  return (
    <ProtectedRoute>
      <RequireReportAccess reportId={reportId}>
        <AppShell>{element}</AppShell>
      </RequireReportAccess>
    </ProtectedRoute>
  );
}

function withProtectedBudgetRead(element: ReactElement) {
  return (
    <ProtectedRoute>
      <RequireContext type="office">
        <BudgetReadRouteGate>{element}</BudgetReadRouteGate>
      </RequireContext>
    </ProtectedRoute>
  );
}

function BudgetReadRouteGate({ children }: { children: ReactElement }) {
  const { isInitialized, isLoading, user } = useAuth();

  if (!isInitialized || isLoading) {
    return <div>Loading...</div>;
  }

  if (!canViewBudgets(user)) {
    return <Navigate replace to="/dashboard" />;
  }

  return <AppShell>{children}</AppShell>;
}

function withProtectedBudgetWorksheet(element: ReactElement) {
  return (
    <ProtectedRoute>
      <RequireContext type="office">
        <BudgetWorksheetRouteGate>{element}</BudgetWorksheetRouteGate>
      </RequireContext>
    </ProtectedRoute>
  );
}

function BudgetWorksheetRouteGate({ children }: { children: ReactElement }) {
  const { isInitialized, isLoading, user } = useAuth();

  if (!isInitialized || isLoading) {
    return <div>Loading...</div>;
  }

  if (!canViewBudgetWorksheets(user)) {
    return <Navigate replace to="/dashboard" />;
  }

  return <AppShell>{children}</AppShell>;
}

function BudgetAllocateRouteGate() {
  const { user } = useAuth();
  const budgetAuthorityQuery = useQuery({
    queryKey: ["budget-authority-config"],
    queryFn: listBudgetAuthorityConfig,
  });

  if (budgetAuthorityQuery.isLoading) {
    return <div>Loading...</div>;
  }

  if (budgetAuthorityQuery.isError) {
    return <div>Unable to verify budget allocation access.</div>;
  }

  if (!canAccessBudgetAllocation(user, budgetAuthorityQuery.data)) {
    return <Navigate replace to="/budget/plan" />;
  }

  return <BudgetAllocatePage />;
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicyPage />,
  },
  {
    path: "/delete-account",
    element: <DeleteAccountPage />,
  },
  {
    path: "/",
    element: withProtectedShell(<Navigate replace to="/dashboard" />),
  },
  {
    path: "/vessel",
    element: withProtectedShell(<Navigate replace to="/dashboard" />),
  },
  {
    path: "/dashboard",
    element: withProtectedShellAndPermissionsAny(<DashboardPage />, {
      forms: [FORM_IDS.DASHBOARD],
    }),
  },
  {
    path: "/owner/dashboard",
    element: withProtectedShellAndPermissions(<DashboardPage />, "office", {
      forms: [FORM_IDS.OWNER_PORTAL],
      processes: [PROCESS_IDS.OWNER_PORTAL_VIEW],
    }),
  },
  {
    path: "/owner/approvals",
    element: withProtectedShellAndPermissions(<OwnerApprovalsPage />, "office", {
      forms: [FORM_IDS.OWNER_PORTAL],
      processes: [PROCESS_IDS.OWNER_PORTAL_VIEW],
    }),
  },
  {
    path: "/owner/vessels/:vesselId",
    element: withProtectedShellAndPermissions(<OwnerVesselDetailPage />, "office", {
      forms: [FORM_IDS.OWNER_PORTAL],
      processes: [PROCESS_IDS.OWNER_PORTAL_VIEW],
    }),
  },
  {
    path: "/requisitions",
    element: withProtectedShellAndPermissionsAny(<RequisitionListPage />, {
      forms: [FORM_IDS.REQUISITIONS],
    }),
  },
  {
    path: "/requisitions/create",
    element: withProtectedShellAndPermissionsAny(<RequisitionCreatePage />, {
      forms: [FORM_IDS.REQUISITIONS],
      processes: [PROCESS_IDS.REQUISITION_CREATE],
    }),
  },
  {
    path: "/requisitions/:id",
    element: withProtectedShellAndPermissionsAny(<RequisitionDetailPage />, {
      forms: [FORM_IDS.REQUISITIONS],
    }),
  },
  {
    path: "/requisitions/:id/edit",
    element: withProtectedShellAndPermissionsAny(<RequisitionEditPage />, {
      forms: [FORM_IDS.REQUISITIONS],
    }),
  },
  {
    path: "/requisitions/:id/hod-review",
    element: withProtectedShellAndPermissionsAny(<HODReviewPage />, {
      forms: [FORM_IDS.REQUISITIONS],
      processes: [PROCESS_IDS.REQUISITION_HOD_REVIEW],
    }),
  },
  {
    path: "/requisitions/:id/review",
    element: withProtectedShellAndPermissions(<RequisitionReviewPage />, "office", {
      forms: [FORM_IDS.REQUISITIONS],
      processes: [PROCESS_IDS.REQUISITION_CATEGORY_REVIEW],
    }),
  },
  {
    path: "/requisitions/:id/timeline",
    element: withProtectedShellAndPermissionsAny(<RequisitionTimelinePage />, {
      forms: [FORM_IDS.REQUISITIONS],
    }),
  },
  {
    path: "/rfqs",
    element: withProtectedShellAndPermissions(<RFQListPage />, "office", {
      forms: [FORM_IDS.RFQS],
    }),
  },
  {
    path: "/rfqs/create",
    element: withProtectedShellAndPermissions(<RFQCreatePage />, "office", {
      forms: [FORM_IDS.RFQS],
      processes: [PROCESS_IDS.RFQ_MANAGE],
    }),
  },
  {
    path: "/rfqs/:id",
    element: withProtectedShellAndPermissions(<RFQDetailPage />, "office", {
      forms: [FORM_IDS.RFQS],
    }),
  },
  {
    path: "/rfqs/:id/responses",
    element: withProtectedShellAndPermissions(<RFQResponseEntryPage />, "office", {
      forms: [FORM_IDS.RFQS],
      processes: [PROCESS_IDS.RFQ_MANAGE],
    }),
  },
  {
    path: "/rfqs/:id/comparison",
    element: withProtectedShellAndPermissions(<QuotationComparisonPage />, "office", {
      anyOf: [
        { forms: [FORM_IDS.RFQS] },
        { processes: [PROCESS_IDS.RFQ_COMPARISON_VIEW] },
      ],
    }),
  },
  {
    path: "/vendors",
    element: withProtectedShellAndPermissions(<VendorListPage />, "office", {
      forms: [FORM_IDS.VENDORS],
    }),
  },
  {
    path: "/vendors/create",
    element: withProtectedShellAndPermissions(<VendorCreatePage />, "office", {
      forms: [FORM_IDS.VENDORS],
      processes: [PROCESS_IDS.VENDOR_MANAGE],
    }),
  },
  {
    path: "/vendors/:id",
    element: withProtectedShellAndPermissions(<VendorDetailPage />, "office", {
      forms: [FORM_IDS.VENDORS],
    }),
  },
  {
    path: "/vendors/:id/edit",
    element: withProtectedShellAndPermissions(<VendorEditPage />, "office", {
      forms: [FORM_IDS.VENDORS],
      processes: [PROCESS_IDS.VENDOR_MANAGE],
    }),
  },
  {
    path: "/vendors/:id/approve",
    element: withProtectedShellAndPermissions(<VendorApprovePage />, "office", {
      forms: [FORM_IDS.VENDORS],
      processes: [PROCESS_IDS.VENDOR_APPROVE],
    }),
  },
  {
    path: "/vendors/:id/contacts",
    element: withProtectedShellAndPermissions(<VendorContactsPage />, "office", {
      forms: [FORM_IDS.VENDORS],
    }),
  },
  {
    path: "/vendors/:id/performance",
    element: withProtectedShellAndPermissions(<VendorPerformancePage />, "office", {
      forms: [FORM_IDS.VENDORS],
    }),
  },
  {
    path: "/vendors/:id/statement-of-account",
    element: withProtectedShellAndPermissions(<VendorStatementOfAccountPage />, "office", {
      forms: [FORM_IDS.VENDORS],
    }),
  },
  {
    path: "/items",
    element: withProtectedShellAndPermissions(<ItemsListPage />, "office", {
      forms: [FORM_IDS.ITEMS],
    }),
  },
  {
    path: "/items/create",
    element: withProtectedShellAndPermissions(<ItemCreatePage />, "office", {
      forms: [FORM_IDS.ITEMS],
      processes: [PROCESS_IDS.ITEM_MANAGE],
    }),
  },
  {
    path: "/items/:id/edit",
    element: withProtectedShellAndPermissions(<ItemEditPage />, "office", {
      forms: [FORM_IDS.ITEMS],
      processes: [PROCESS_IDS.ITEM_MANAGE],
    }),
  },
  {
    path: "/inventory",
    element: withProtectedShellAndPermissions(<InventoryAdminPage />, "office", {
      forms: [FORM_IDS.INVENTORY],
      processes: [PROCESS_IDS.INVENTORY_ADMIN_MANAGE],
    }),
  },
  {
    path: "/services",
    element: withProtectedShellAndPermissions(<ServiceListPage />, "office", {
      forms: [FORM_IDS.SERVICES],
    }),
  },
  {
    path: "/services/create",
    element: withProtectedShellAndPermissions(<ServiceCreatePage />, "office", {
      forms: [FORM_IDS.SERVICES],
      processes: [PROCESS_IDS.SERVICE_MANAGE],
    }),
  },
  {
    path: "/services/:id/edit",
    element: withProtectedShellAndPermissions(<ServiceEditPage />, "office", {
      forms: [FORM_IDS.SERVICES],
      processes: [PROCESS_IDS.SERVICE_MANAGE],
    }),
  },
  {
    path: "/services/requests/:id",
    element: withProtectedShellAndPermissions(<ServiceRequestReviewPage />, "office", {
      forms: [FORM_IDS.SERVICES],
      processes: [PROCESS_IDS.SERVICE_MANAGE],
    }),
  },
  {
    path: "/purchase-orders",
    element: withProtectedShellAndPermissionsAny(<PurchaseOrderListPage />, {
      forms: [FORM_IDS.PURCHASE_ORDERS],
    }),
  },
  {
    path: "/contracts",
    element: withProtectedShellAndPermissions(<ContractsListPage />, "office", {
      forms: [FORM_IDS.CONTRACTS],
      processes: [PROCESS_IDS.CONTRACT_VIEW],
    }),
  },
  {
    path: "/contracts/create",
    element: withProtectedShellAndPermissions(<ContractCreatePage />, "office", {
      forms: [FORM_IDS.CONTRACTS],
      processes: [PROCESS_IDS.CONTRACT_MANAGE],
    }),
  },
  {
    path: "/contracts/:id",
    element: withProtectedShellAndPermissions(<ContractDetailPage />, "office", {
      forms: [FORM_IDS.CONTRACTS],
      processes: [PROCESS_IDS.CONTRACT_VIEW],
    }),
  },
  {
    path: "/contracts/:id/edit",
    element: withProtectedShellAndPermissions(<ContractEditPage />, "office", {
      forms: [FORM_IDS.CONTRACTS],
      processes: [PROCESS_IDS.CONTRACT_MANAGE],
    }),
  },
  {
    path: "/purchase-orders/create",
    element: withProtectedShellAndPermissions(<PurchaseOrderCreatePage />, "office", {
      forms: [FORM_IDS.PURCHASE_ORDERS],
      processes: [PROCESS_IDS.PO_CREATE],
    }),
  },
  {
    path: "/purchase-orders/create-direct",
    element: withProtectedShellAndPermissions(<PurchaseOrderCreateDirectPage />, "office", {
      forms: [FORM_IDS.PURCHASE_ORDERS],
      processes: [PROCESS_IDS.PO_CREATE],
    }),
  },
  {
    path: "/purchase-orders/:id",
    element: withProtectedShellAndPermissionsAny(<PurchaseOrderDetailPage />, {
      forms: [FORM_IDS.PURCHASE_ORDERS],
    }),
  },
  {
    path: "/purchase-orders/:id/tracker",
    element: withProtectedShellAndPermissionsAny(<TrackerDetailPage />, {
      forms: [FORM_IDS.TRACKER],
    }),
  },
  {
    path: "/purchase-orders/:id/edit",
    element: withProtectedShellAndPermissions(<PurchaseOrderEditPage />, "office", {
      forms: [FORM_IDS.PURCHASE_ORDERS],
      processes: [PROCESS_IDS.PO_CREATE],
    }),
  },
  {
    path: "/purchase-orders/:id/approve",
    element: withProtectedShellAndPermissions(
      <PurchaseOrderApprovalPage />,
      "office",
      {
        forms: [FORM_IDS.PURCHASE_ORDERS],
        processes: [PROCESS_IDS.PO_APPROVE],
      },
    ),
  },
  {
    path: "/purchase-orders/:id/revisions",
    element: withProtectedShellAndPermissionsAny(<PurchaseOrderRevisionsPage />, {
      forms: [FORM_IDS.PURCHASE_ORDERS],
    }),
  },
  {
    path: "/invoices",
    element: withProtectedShellAndPermissions(<InvoiceRegisterPage />, "office", {
      forms: [FORM_IDS.INVOICES],
    }),
  },
  {
    path: "/invoices/configuration",
    element: withProtectedShellAndPermissions(<InvoiceConfigurationPage />, "office", {
      forms: [FORM_IDS.INVOICE_CONFIG],
      processes: [PROCESS_IDS.INVOICE_CONFIG_MANAGE],
    }),
  },
  {
    path: "/invoices/create/:poId",
    element: withProtectedShellAndPermissions(<InvoiceCreatePage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_VERIFY],
    }),
  },
  {
    path: "/invoices/payment-batches/review",
    element: withProtectedShellAndPermissions(<InvoicePaymentBatchReviewPage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_APPROVE],
    }),
  },
  {
    path: "/invoices/:id",
    element: withProtectedShellAndPermissions(<InvoiceDetailPage />, "office", {
      forms: [FORM_IDS.INVOICES],
    }),
  },
  {
    path: "/invoices/:id/edit",
    element: withProtectedShellAndPermissions(<InvoiceEditPage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_VERIFY],
    }),
  },
  {
    path: "/invoices/:invoiceId/purchase-orders/:id",
    element: withProtectedShellAndPermissions(<PurchaseOrderDetailPage />, "office", {
      forms: [FORM_IDS.INVOICES],
    }),
  },
  {
    path: "/invoices/:invoiceId/grns/:id",
    element: withProtectedShellAndPermissions(<GRNDetailPage />, "office", {
      forms: [FORM_IDS.INVOICES],
    }),
  },
  {
    path: "/invoices/capture/purchase-orders/:id",
    element: withProtectedShellAndPermissions(<PurchaseOrderDetailPage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_VERIFY],
    }),
  },
  {
    path: "/invoices/capture/grns/:id",
    element: withProtectedShellAndPermissions(<GRNDetailPage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_VERIFY],
    }),
  },
  {
    path: "/invoices/capture/service-completions/:id",
    element: withProtectedShellAndPermissions(<ServiceCompletionDetailPage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_VERIFY],
    }),
  },
  {
    path: "/invoices/:id/verify",
    element: withProtectedShellAndPermissions(<InvoiceVerifyPage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_VERIFY],
    }),
  },
  {
    path: "/invoices/:id/approve",
    element: withProtectedShellAndPermissions(<InvoiceApprovePage />, "office", {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_APPROVE],
    }),
  },
  {
    path: "/invoices/:id/payment",
    element: withProtectedShellAndPermissions(<InvoicePaymentPage />, "office", {
      forms: [FORM_IDS.INVOICES],
    }),
  },
  {
    path: "/credit-notes",
    element: withProtectedShellAndPermissions(<CreditNoteListPage />, "office", {
      forms: [FORM_IDS.CREDIT_NOTES],
      processes: [PROCESS_IDS.CREDIT_NOTE_VIEW],
    }),
  },
  {
    path: "/credit-notes/:id",
    element: withProtectedShellAndPermissions(<CreditNoteDetailPage />, "office", {
      forms: [FORM_IDS.CREDIT_NOTES],
      processes: [PROCESS_IDS.CREDIT_NOTE_VIEW],
    }),
  },
  {
    path: "/budget",
    element: withProtectedBudgetRead(<BudgetOverviewPage />),
  },
  {
    path: "/budget/plan",
    element: withProtectedBudgetRead(<BudgetPlanListPage />),
  },
  {
    path: "/budget/plan/create",
    element: withProtectedShellAndPermissions(<BudgetPlanCreatePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_MANAGE],
    }),
  },
  {
    path: "/budget/plan/:budgetId",
    element: withProtectedBudgetRead(<BudgetPlanDetailPage />),
  },
  {
    path: "/budget/drydock",
    element: withProtectedBudgetRead(<DrydockBudgetListPage />),
  },
  {
    path: "/projects",
    element: withProtectedShellAndPermissions(<ProjectsPage />, "office", {
      forms: [FORM_IDS.PROJECTS],
      processes: [PROCESS_IDS.PROJECT_VIEW],
    }),
  },
  {
    path: "/projects/:projectId",
    element: withProtectedShellAndPermissions(<ProjectDetailPage />, "office", {
      forms: [FORM_IDS.PROJECTS],
      processes: [PROCESS_IDS.PROJECT_VIEW, PROCESS_IDS.PROJECT_MANAGE],
    }),
  },
  {
    path: "/budget/drydock/create",
    element: withProtectedShellAndPermissions(<DrydockBudgetCreatePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_MANAGE],
    }),
  },
  {
    path: "/budget/drydock/:budgetId",
    element: withProtectedBudgetRead(<BudgetPlanDetailPage />),
  },
  {
    path: "/budget/drydock/:budgetId/worksheet",
    element: withProtectedBudgetWorksheet(<DrydockWorksheetPage />),
  },
  {
    path: "/budget/drydock/:budgetId/tracking",
    element: withProtectedBudgetRead(<DrydockTrackingPage />),
  },
  {
    path: "/budget/predelivery",
    element: withProtectedBudgetRead(<PredeliveryBudgetListPage />),
  },
  {
    path: "/budget/predelivery/create",
    element: withProtectedShellAndPermissions(<PredeliveryBudgetCreatePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_MANAGE],
    }),
  },
  {
    path: "/budget/recovery",
    element: withProtectedBudgetRead(<RecoveryEntryListPage />),
  },
  {
    path: "/budget/recovery/create",
    element: withProtectedShellAndPermissions(<RecoveryEntryCreatePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_MANAGE],
    }),
  },
  {
    path: "/budget/predelivery/:budgetId",
    element: withProtectedBudgetRead(<BudgetPlanDetailPage />),
  },
  {
    path: "/budget/predelivery/:budgetId/worksheet",
    element: withProtectedBudgetWorksheet(<PredeliveryWorksheetPage />),
  },
  {
    path: "/budget/predelivery/:budgetId/tracking",
    element: withProtectedBudgetRead(<PredeliveryTrackingPage />),
  },
  {
    path: "/budget/plan/:budgetId/worksheet",
    element: withProtectedBudgetWorksheet(<BudgetWorksheetPage />),
  },
  {
    path: "/budget/plan/:budgetId/submit",
    element: withProtectedShellAndPermissions(<BudgetSubmitPage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_MANAGE],
    }),
  },
  {
    path: "/budget/plan/:budgetId/revise",
    element: withProtectedShellAndPermissions(<BudgetPlanRevisePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_MANAGE],
    }),
  },
  {
    path: "/budget/plan/:budgetId/activate",
    element: withProtectedShellAndPermissions(<BudgetActivatePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_ACTIVATE],
    }),
  },
  {
    path: "/budget/approvals",
    element: withProtectedShellAndPermissions(<BudgetApprovalsPage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_APPROVE],
    }),
  },
  {
    path: "/budget/plan/:budgetId/approve",
    element: withProtectedShellAndPermissions(<BudgetApprovePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_APPROVE],
    }),
  },
  {
    path: "/budget/plan/:budgetId/exclusions",
    element: withProtectedShellAndPermissions(<BudgetExclusionsPage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_MANAGE],
    }),
  },
  {
    path: "/budget/allocate",
    element: withProtectedShellAndPermissions(<BudgetAllocateRouteGate />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_ALLOCATE],
    }),
  },
  {
    path: "/budget/revise/:allocationId",
    element: withProtectedShellAndPermissions(<BudgetRevisePage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_REVISION_REQUEST],
    }),
  },
  {
    path: "/budget/close-year",
    element: withProtectedShellAndPermissions(<BudgetCloseYearPage />, "office", {
      forms: [FORM_IDS.BUDGETS],
      processes: [PROCESS_IDS.BUDGET_CLOSE],
    }),
  },
  {
    path: "/budget/:vesselId/:accountCode",
    element: withProtectedBudgetRead(<BudgetDetailPage />),
  },
  {
    path: "/exchange-rates",
    element: withProtectedShellAndPermissions(
      <ExchangeRateListPage />,
      "office",
      {
        forms: [FORM_IDS.EXCHANGE_RATES],
        processes: [PROCESS_IDS.EXCHANGE_RATE_VIEW, PROCESS_IDS.EXCHANGE_RATE_MANAGE],
      },
    ),
  },
  {
    path: "/exchange-rates/entry",
    element: withProtectedShellAndPermissions(<ExchangeRateEntryPage />, "office", {
      forms: [FORM_IDS.EXCHANGE_RATES],
      processes: [PROCESS_IDS.EXCHANGE_RATE_MANAGE],
    }),
  },
  {
    path: "/exchange-rates/history/:currency",
    element: withProtectedShellAndPermissions(
      <ExchangeRateHistoryPage />,
      "office",
      {
        forms: [FORM_IDS.EXCHANGE_RATES],
        processes: [PROCESS_IDS.EXCHANGE_RATE_VIEW, PROCESS_IDS.EXCHANGE_RATE_MANAGE],
      },
    ),
  },
  {
    path: "/admin/delegations",
    element: withProtectedShellAndPermissions(<DelegationListPage />, "office", {
      forms: [FORM_IDS.DELEGATIONS],
      processes: [PROCESS_IDS.DELEGATION_MANAGE],
    }),
  },
  {
    path: "/admin/delegations/create",
    element: withProtectedShellAndPermissions(<DelegationCreatePage />, "office", {
      forms: [FORM_IDS.DELEGATIONS],
      processes: [PROCESS_IDS.DELEGATION_MANAGE],
    }),
  },
  {
    path: "/admin/users",
    element: withProtectedShellAndPermissions(<UserManagementPage />, "office", {
      forms: [FORM_IDS.ADMIN_USERS],
      processes: [PROCESS_IDS.ADMIN_USER_MANAGE],
    }),
  },
  {
    path: "/admin/users/create",
    element: withProtectedShellAndPermissions(<UserCreatePage />, "office", {
      forms: [FORM_IDS.ADMIN_USERS],
      processes: [PROCESS_IDS.ADMIN_USER_MANAGE],
    }),
  },
  {
    path: "/admin/users/:id/edit",
    element: withProtectedShellAndPermissions(<UserEditPage />, "office", {
      forms: [FORM_IDS.ADMIN_USERS],
      processes: [PROCESS_IDS.ADMIN_USER_MANAGE],
    }),
  },
  {
    path: "/admin/roles",
    element: withProtectedShellAndPermissions(<RoleAssignmentPage />, "office", {
      forms: [FORM_IDS.ADMIN_USERS],
      processes: [PROCESS_IDS.ADMIN_USER_MANAGE],
    }),
  },
  {
    path: "/admin/system-config",
    element: withProtectedShellAndPermissions(<SystemConfigPage />, "office", {
      forms: [FORM_IDS.SYSTEM_CONFIG],
      processes: [PROCESS_IDS.SYSTEM_CONFIG_MANAGE],
    }),
  },
  {
    path: "/admin/approval-chains",
    element: withProtectedShellAndPermissions(<ApprovalChainConfigPage />, "office", {
      forms: [FORM_IDS.APPROVAL_CHAINS],
      processes: [PROCESS_IDS.APPROVAL_CHAIN_MANAGE],
    }),
  },
  {
    path: "/admin/account-codes",
    element: withProtectedShellAndPermissions(<AccountCodeManagementPage />, "office", {
      forms: [FORM_IDS.ACCOUNT_CODES],
      processes: [PROCESS_IDS.ACCOUNT_CODE_VIEW],
    }),
  },
  {
    path: "/admin/account-codes/create",
    element: withProtectedShellAndPermissions(<AccountCodeCreatePage />, "office", {
      forms: [FORM_IDS.ACCOUNT_CODES],
      processes: [PROCESS_IDS.ACCOUNT_CODE_CREATE],
    }),
  },
  {
    path: "/admin/account-codes/:id/edit",
    element: withProtectedShellAndPermissions(<AccountCodeEditPage />, "office", {
      forms: [FORM_IDS.ACCOUNT_CODES],
      processes: [PROCESS_IDS.ACCOUNT_CODE_MANAGE],
    }),
  },
  {
    path: "/admin/expenditure-types/create",
    element: withProtectedShellAndPermissions(<ExpenditureTypeCreatePage />, "office", {
      forms: [FORM_IDS.ACCOUNT_CODES],
      processes: [PROCESS_IDS.EXPENDITURE_TYPE_MANAGE],
    }),
  },
  {
    path: "/admin/expenditure-types/:id/edit",
    element: withProtectedShellAndPermissions(<ExpenditureTypeEditPage />, "office", {
      forms: [FORM_IDS.ACCOUNT_CODES],
      processes: [PROCESS_IDS.EXPENDITURE_TYPE_MANAGE],
    }),
  },
  {
    path: "/admin/category-reviewers",
    element: <Navigate replace to="/admin/approval-chains" />,
  },
  {
    path: "/admin/contract-approval-chains",
    element: withProtectedShellAndPermissions(<ContractApprovalChainConfigPage />, "office", {
      forms: [FORM_IDS.CONTRACT_CHAINS],
      processes: [PROCESS_IDS.CONTRACT_CHAIN_MANAGE],
    }),
  },
  {
    path: "/notifications",
    element: withProtectedShell(<NotificationCenterPage />),
  },
  {
    path: "/my-comments",
    element: withProtectedShell(<MyCommentsPage />),
  },
  {
    path: "/reports/requisition-register",
    element: withProtectedReport(<RequisitionRegisterReportPage />, "requisition-register"),
  },
  {
    path: "/reports/po-register",
    element: withProtectedReport(<PORegisterReportPage />, "po-register"),
  },
  {
    path: "/reports/vendor-performance",
    element: withProtectedReport(<VendorPerformanceReportPage />, "vendor-performance"),
  },
  {
    path: "/reports/vendor-statement-of-account",
    element: withProtectedReport(<VendorStatementOfAccountReportPage />, "vendor-statement-of-account"),
  },
  {
    path: "/reports/budget-utilization",
    element: withProtectedReport(<BudgetUtilizationReportPage />, "budget-utilization"),
  },
  {
    path: "/reports/invoice-aging",
    element: withProtectedReport(<InvoiceAgingReportPage />, "invoice-aging"),
  },
  {
    path: "/reports/paid-awaiting-delivery",
    element: withProtectedReport(<PaidAwaitingDeliveryReportPage />, "paid-awaiting-delivery"),
  },
  {
    path: "/reports/supply-by-port",
    element: withProtectedReport(<SupplyByPortReportPage />, "supply-by-port"),
  },
  {
    path: "/audit",
    element: withProtectedShellAndPermissions(<AuditLogPage />, "office", {
      forms: [FORM_IDS.AUDIT],
      processes: [PROCESS_IDS.AUDIT_VIEW],
    }),
  },
  {
    path: "/audit/export",
    element: withProtectedShellAndPermissions(<AuditExportPage />, "office", {
      forms: [FORM_IDS.AUDIT],
      processes: [PROCESS_IDS.AUDIT_VIEW],
    }),
  },
  {
    path: "/grns",
    element: withProtectedShellAndPermissionsAny(<GRNListPage />, {
      forms: [FORM_IDS.GRN],
    }),
  },
  {
    path: "/grns/create/:poId",
    element: withProtectedShellAndPermissions(<GRNCreatePage />, "crew", {
      forms: [FORM_IDS.GRN],
      processes: [PROCESS_IDS.GRN_CREATE],
    }),
  },
  {
    path: "/grns/:id",
    element: withProtectedShellAndPermissionsAny(<GRNDetailPage />, {
      forms: [FORM_IDS.GRN, FORM_IDS.TRACKER],
    }),
  },
  {
    path: "/service-completions/create/:poId",
    element: withProtectedShellAndPermissions(<ServiceCompletionCreatePage />, "crew", {
      forms: [FORM_IDS.TRACKER],
      processes: [PROCESS_IDS.SERVICE_COMPLETION_CREATE],
    }),
  },
  {
    path: "/service-completions/:id",
    element: withProtectedShellAndPermissionsAny(<ServiceCompletionDetailPage />, {
      forms: [FORM_IDS.TRACKER],
    }),
  },
]);
